import {
  keepPreviousData,
  useInfiniteQuery,
  type InfiniteData,
  type UseInfiniteQueryResult,
} from '@tanstack/react-query';
import type {
  ConversationFilter,
  ConversationItem,
  ConversationListResponse,
} from '@chatapp-inbox/shared';
import { listConversations } from '../../api';

/** Tamanho de cada página pedida à API. */
export const PAGE_SIZE = 30;

/**
 * Chave do cache de cada aba. O tempo real (T26) atualiza ESTES caches com `setQueryData`, cujo
 * formato é `InfiniteData<ConversationListResponse, string | null>`: não mude essa estrutura.
 */
export const conversationsKey = (filter: ConversationFilter) => ['conversations', filter] as const;

/**
 * Chave do cache de uma BUSCA. Fica fora de `['conversations', …]` de propósito: o tempo real mexe nas
 * listas daquela chave posição a posição (uma conversa nova entra, outra sobe), e numa busca isso poria
 * conversas que não casam com o texto. Os resultados de busca são refeitos em vez de remendados.
 */
export const conversationSearchKey = (filter: ConversationFilter, q: string) =>
  ['conversation-search', filter, q] as const;

type CachedPages = InfiniteData<ConversationListResponse, string | null>;

/** Dados entregues pelo hook: as páginas como estão no cache + a lista achatada e sem repetição. */
export type ConversationsData = CachedPages & { conversations: ConversationItem[] };

/**
 * Concatena as páginas mantendo a ordem da API. Uma conversa que recebe mensagem entre duas
 * buscas reaparece na página seguinte; vale a PRIMEIRA ocorrência (a mais recente).
 */
function flattenUnique(pages: readonly ConversationListResponse[]): ConversationItem[] {
  const seen = new Set<string>();
  const result: ConversationItem[] = [];
  for (const page of pages) {
    for (const conversation of page.conversations) {
      if (seen.has(conversation.id)) continue;
      seen.add(conversation.id);
      result.push(conversation);
    }
  }
  return result;
}

// Função estável (fora do hook): o React Query só recalcula quando os dados mudam, então a lista
// achatada mantém a mesma identidade entre renderizações e os itens memorizados não são redesenhados.
const selectConversations = (data: CachedPages): ConversationsData => ({
  ...data,
  conversations: flattenUnique(data.pages),
});

/**
 * Lista de conversas de uma aba, paginada por cursor. A lista achatada e SEM duplicatas está em
 * `data.conversations`; a ordem é a da API (mais recente primeiro), nunca reordenada no cliente.
 */
export function useConversations(
  filter: ConversationFilter,
  /** O que a pessoa procura (já limpo, ver `parseSearch`); vazio = a lista da aba. */
  q = '',
): UseInfiniteQueryResult<ConversationsData, Error> {
  const searching = q !== '';
  return useInfiniteQuery({
    queryKey: searching ? conversationSearchKey(filter, q) : conversationsKey(filter),
    queryFn: ({ pageParam, signal }) =>
      listConversations({
        filter,
        ...(searching ? { q } : {}),
        cursor: pageParam,
        limit: PAGE_SIZE,
        signal,
      }),
    // Enquanto digita, o resultado anterior fica na tela (esmaecido) em vez de piscar o esqueleto.
    ...(searching ? { placeholderData: keepPreviousData } : {}),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    select: selectConversations,
  });
}
